/**
 * Statsig Analytics Manager
 * Tracks funnel events and user behavior
 */

import type {
  StatsigClient,
  StatsigUser,
  StatsigOptions,
  AnalyticsEventData,
  ConsentManager,
} from '../types/analytics.types';
import type { UserRepository } from '../data/user-repository';
import type { Config } from '../types/config.types';
import type { StatsManager } from '../core/stats-manager';
import type { EventName, EventData, EventMetadata } from '../types/events.types';
import { getRolloutInfo } from '../core/rollout.ts';
import { BUILD_SHA, BUILD_BRANCH } from '../core/build-info.ts';

/**
 * Dependencies for StatsigManager
 */
interface StatsigDeps {
  userRepository: UserRepository;
  config: Config;
  statsManager: StatsManager;
  consentManager: ConsentManager;
}

/**
 * Statsig Manager
 */
export class StatsigManager {
  private client: StatsigClient | null = null;
  private initialized = false;
  private userIdentified = false;
  private deps: Partial<StatsigDeps> = {};

  /**
   * Initialize Statsig - now accepts dependencies
   */
  async init(deps: Partial<StatsigDeps>): Promise<void> {
    // Validate required dependencies
    if (!deps.userRepository || !deps.config || !deps.statsManager || !deps.consentManager) {
      throw new Error(
        'StatsigManager.init() requires userRepository, config, statsManager, and consentManager dependencies'
      );
    }

    this.deps = deps;

    if (!this.deps.config!.analytics.enabled || !this.deps.config!.analytics.statsig.clientKey) {
      console.log('Statsig analytics disabled or not configured');
      return;
    }

    // Statsig is essential product analytics: gate on essential consent (loads on "Accept All" OR
    // "Essential Only", or where consent isn't required). The debug flag forces eager init.
    if (this.deps.config!.analytics.hardcoreStartupDebug) {
      await this.loadAndSubscribe();
    } else {
      this.deps.consentManager!.onEssentialConsentGranted(() => {
        void this.loadAndSubscribe();
      });
    }
  }

  /**
   * Create the Statsig client and subscribe to StatsManager. Runs once, when essential consent is
   * granted — or immediately under hardcoreStartupDebug.
   */
  private async loadAndSubscribe(): Promise<void> {
    if (this.initialized) {
      return;
    }

    try {
      // Get Statsig from global window object (loaded via CDN)
      const { StatsigClient, runStatsigAutoCapture, runStatsigSessionReplay } = window.Statsig!;

      // Create client with anonymous user initially
      // Map environment to Statsig tier ('sandbox' -> 'development', 'production' -> 'production')
      const statsigTier =
        this.deps.config!.environment === 'sandbox' ? 'development' : 'production';

      // Use customIDs for anonymous tracking before email is captured
      const anonymousID = this.generateAnonymousID();

      const user: StatsigUser = {
        // Leave userID empty/undefined until email is captured
        customIDs: {
          anonymousID: anonymousID,
          sessionID: this.generateSessionID(),
        },
        custom: this.buildCustomFields(),
      };

      const options: StatsigOptions = {
        environment: {
          tier: statsigTier as 'production' | 'development',
        },
      };

      this.client = new StatsigClient(
        this.deps.config!.analytics.statsig.clientKey,
        user,
        options
      );

      // Enable auto-capture if configured
      if (this.deps.config!.analytics.statsig.enableAutoCapture) {
        runStatsigAutoCapture(this.client);
      }

      // Enable session replay if configured
      if (this.deps.config!.analytics.statsig.enableSessionReplay) {
        runStatsigSessionReplay(this.client);
      }

      // Initialize the client
      await this.client.initializeAsync();
      this.initialized = true;

      console.log('Statsig initialized successfully');
      // Subscribe to StatsManager events
      this.deps.statsManager!.subscribe(this.handleStatsManagerEvent.bind(this));
      console.log('Statsig subscribed to StatsManager events');
    } catch (error) {
      console.error('Failed to initialize Statsig:', error);
    }
  }

  /**
   * Handle events from StatsManager
   */
  private handleStatsManagerEvent(
    eventName: EventName,
    eventData: EventData,
    _eventMetadata: EventMetadata,
    value: string | number | null
  ): void {
    if (!this.initialized || !this.client) {
      console.warn('Statsig not initialized or client not found, skipping event:', eventName);
      return;
    }

    try {
      // Handle user identification separately
      if (eventName === 'user_identified' && eventData.email) {
        this.identifyUser(eventData.email as string);
        return;
      }

      // Track all other events with value if provided
      this.trackEvent(eventName, eventData as AnalyticsEventData, value);
    } catch (error) {
      console.error('Statsig: Failed to handle event:', eventName, error);
    }
  }

  /**
   * Update dependencies after initialization (useful for circular dependencies)
   */
  updateDependencies(deps: Partial<StatsigDeps>): void {
    this.deps = { ...this.deps, ...deps };
  }

  /**
   * Shared `custom` fields for the StatsigUser: campaign params + A/B variant + rollout arm
   * and build identity (parity with AmplitudeManager's initial user properties). Rollout keys
   * are absent outside an active rollout (no voc_rollout cookie).
   */
  private buildCustomFields(): Record<string, string> {
    const rollout = getRolloutInfo();
    return {
      ...this.deps.userRepository!.getCampaignParams(),
      ab_test_variant: this.deps.userRepository!.getABTestVariant(),
      ...(rollout ? { rollout_arm: rollout.arm, rollout_epoch: String(rollout.epoch) } : {}),
      build_sha: BUILD_SHA,
      build_branch: BUILD_BRANCH,
    };
  }

  /**
   * Generate or retrieve anonymous user ID
   */
  private generateAnonymousID(): string {
    let anonymousID = localStorage.getItem('vocalist_anonymous_id');
    if (!anonymousID) {
      anonymousID =
        'anon_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('vocalist_anonymous_id', anonymousID);
    }
    return anonymousID;
  }

  /**
   * Generate or retrieve session ID (expires after session)
   */
  private generateSessionID(): string {
    let sessionID = sessionStorage.getItem('vocalist_session_id');
    if (!sessionID) {
      sessionID =
        'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      sessionStorage.setItem('vocalist_session_id', sessionID);
    }
    return sessionID;
  }

  /**
   * Update user identity when email is captured
   */
  async identifyUser(email: string): Promise<void> {
    if (!this.initialized || !this.client || this.userIdentified) {
      return;
    }

    try {
      // Keep customIDs for tracking anonymous → identified user journey
      const user: StatsigUser = {
        userID: email, // Now use email as primary userID
        email: email,
        customIDs: {
          anonymousID: this.generateAnonymousID(), // Preserve anonymous ID for journey tracking
          sessionID: this.generateSessionID(), // Keep session ID
        },
        custom: this.buildCustomFields(),
      };

      await this.client.updateUserAsync(user);

      this.userIdentified = true;
      console.log('Statsig user identified:', email);

      // Track email capture event
      this.trackEvent('email_captured', { email });
    } catch (error) {
      console.error('Failed to identify user in Statsig:', error);
    }
  }

  /**
   * Track custom event
   */
  trackEvent(
    eventName: string,
    metadata: AnalyticsEventData = {},
    value: string | number | null = null
  ): void {
    if (!this.initialized || !this.client) {
      console.warn('Statsig not initialized or client not found');
      return;
    }
    try {
      this.client.logEvent(eventName, value, metadata);
      console.log('Statsig event:', eventName, metadata);
    } catch (error) {
      console.error('Failed to track event:', error);
    }
  }

  /**
   * Track screen view
   */
  trackScreenView(screenName: string, screenHash: string): void {
    const metadata: AnalyticsEventData = {
      screen: screenName,
      screenHash: screenHash,
    };
    if (window.location.search.includes('email=')) {
      metadata.hasEmailParam = true;
    }
    this.trackEvent('screen_viewed', metadata, screenHash);
  }

  /**
   * Track funnel progression
   */
  trackFunnelStep(step: string, data: AnalyticsEventData = {}): void {
    this.trackEvent(
      'funnel_step_completed',
      {
        step,
        ...data,
      },
      step
    );
  }

  /**
   * Track answer selection
   */
  trackAnswer(questionId: string, answer: string | string[], questionType = 'single'): void {
    this.trackEvent('question_answered', {
      questionId,
      answer,
      questionType,
    });
  }

  /**
   * Track genre selection (multi-select)
   */
  trackGenreSelection(genres: string[]): void {
    this.trackEvent('genres_selected', {
      genres: genres.join(', '),
      genreCount: genres.length,
    });
  }

  /**
   * Track practice commitment
   */
  trackPracticeCommitment(frequency: string): void {
    this.trackEvent('practice_commitment_selected', {
      frequency,
    });
  }

  /**
   * Track paywall view
   */
  trackPaywallView(timerStarted = false): void {
    const userData = this.deps.userRepository!.getAllData();
    this.trackEvent('paywall_viewed', {
      timerStarted,
      plan: (userData as any).plan || '',
    });
  }

  /**
   * Track checkout initiated
   */
  trackCheckoutInitiated(price: number, currency = 'USD'): void {
    const userData = this.deps.userRepository!.getAllData();
    this.trackEvent(
      'checkout_initiated',
      {
        plan: (userData as any).plan || '',
        price,
        currency,
      },
      price + ' ' + currency
    );
  }

  /**
   * Track country change in Paddle
   */
  trackCountryChange(
    oldCountry: string,
    newCountry: string,
    oldCurrency: string,
    newCurrency: string
  ): void {
    this.trackEvent('country_changed', {
      oldCountry,
      newCountry,
      oldCurrency,
      newCurrency,
    });
  }

  /**
   * Track subscription completed
   */
  trackSubscriptionCompleted(transactionId: string, amount: number, currency: string): void {
    const userData = this.deps.userRepository!.getAllData();
    this.trackEvent(
      'subscription_completed',
      {
        transactionId,
        amount,
        currency,
        plan: (userData as any).plan || '',
      },
      amount + ' ' + currency
    );
  }

  /**
   * Track timer events
   */
  trackTimerStarted(duration: number): void {
    this.trackEvent('timer_started', {
      durationMinutes: duration,
    });
  }

  trackTimerExpired(): void {
    this.trackEvent('timer_expired');
  }

  /**
   * Track CTA clicks
   */
  trackCTAClick(ctaName: string, location: string): void {
    this.trackEvent('cta_clicked', {
      ctaName,
      location,
    });
  }

  /**
   * Track email validation failure
   */
  trackEmailValidationFailed(email: string): void {
    this.trackEvent('email_validation_failed', {
      emailPattern: email.replace(/@.*/, '@***'), // Anonymize domain
    });
  }

  /**
   * Track direct paywall access
   */
  trackDirectPaywallAccess(source: string): void {
    this.trackEvent('direct_paywall_access', { source }, source);
  }

  /**
   * Track navigation back
   */
  trackNavigationBack(fromScreen: string, toScreen: string): void {
    this.trackEvent('navigation_back', {
      fromScreen,
      toScreen,
    });
  }

  /**
   * Track plan selection change
   */
  trackPlanChange(oldPlan: string, newPlan: string): void {
    this.trackEvent('plan_changed', {
      oldPlan,
      newPlan,
    });
  }

  /**
   * Track family plan toggle
   */
  trackFamilyPlanToggle(enabled: boolean): void {
    this.trackEvent('family_plan_toggled', { enabled }, enabled ? 'true' : 'false');
  }

  /**
   * Shutdown (called on page unload)
   */
  shutdown(): void {
    if (this.initialized && this.client) {
      try {
        this.client.shutdown();
      } catch (error) {
        console.error('Failed to shutdown Statsig:', error);
      }
    }
  }
}

// Create singleton instance
export const statsigManager = new StatsigManager();

// Auto-shutdown on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    statsigManager.shutdown();
  });
}
